import {ExtraServiceEmailResource, ExtraServiceUrlResource, ExtrasResourceDto} from '@lib/infrastructure';
import {AllPrefillFields, PREFILL_FIELDS_BY_CATEGORY} from '@lib/utils';

type Data = Partial<Record<AllPrefillFields, string | number | undefined>>;

export const transformResourceToUrl = (data: Data, resource?: ExtrasResourceDto) => {
  switch (resource?.type) {
    case 'Url':
      return resource.url;
    case 'Email':
      return transformResourceToEmailUrl(resource, data);
    case 'Form':
      return transformFormResourceToUrl(resource, data);
    default:
      return '';
  }
};

const transformResourceToEmailUrl = (resource: ExtraServiceEmailResource, data: Data) => {
  const subject = replacePrefillFields(resource.subject, data);
  const content = replacePrefillFields(resource.content ?? '', data);

  const mailtoLink = `mailto:${resource.to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(content)}`;

  return mailtoLink;
};

const transformFormResourceToUrl = (resource: ExtraServiceUrlResource, data: Data) => {
  if (resource.type === 'Url') return resource.url;

  const decodedUrl = decodeURIComponent(fullyDecodeHtml(resource.url));

  const replacedUrl = replacePrefillFields(decodedUrl, data);
  const encodedUrl = encodeURI(replacedUrl);

  const url = new URL(replacedUrl).toString();

  return url;
};

const replacePrefillFields = (content: string, data: Data): string => {
  const fields = new Set(Object.values(PREFILL_FIELDS_BY_CATEGORY).flatMap((f) => f));

  return content.replace(/\[([^\[\]]+)\]/g, (match, field) => {
    const trimmed = field.trim();
    if (!fields.has(trimmed)) return match;

    const trimmedAsValidKey = trimmed as AllPrefillFields;
    if (data[trimmedAsValidKey] !== undefined) {
      return `${data[trimmedAsValidKey]}`;
    }

    return match; // leave as is if not valid
  });
};

const decodeHtml = (input: string): string =>
  input
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");

const fullyDecodeHtml = (input: string): string => {
  let prev: string;
  let current = input;
  do {
    prev = current;
    current = decodeHtml(current);
  } while (current !== prev);
  return current;
};
